/**
 * Cap Table Simulator — investor equity calculator for the Pitch Deck.
 *
 * Per user spec: a FREE TEXT input for the investment amount plus 4 quick
 * shortcut buttons ($25K / $50K / $100K / $250K). Equity math:
 *   post-money = pre-money + investment
 *   investor % = investment / post-money
 */
import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Calculator } from "lucide-react";
import { Overline } from "@/components/ui-kit";

const GREEN = "#2563EB";
const GOLD = "#B89154";

const SHORTCUTS = [
  { label: "$25K", value: 25000 },
  { label: "$50K", value: 50000 },
  { label: "$100K", value: 100000 },
  { label: "$250K", value: 250000 },
];

/** Tolerant parser: accepts "50000", "50.000", "50,000", "€50k", "1.5m"… */
export function parseAmount(raw) {
  if (raw === null || raw === undefined) return 0;
  let s = String(raw).trim().toLowerCase().replace(/[€$\s]/g, "");
  if (!s) return 0;
  let mult = 1;
  if (s.endsWith("k")) { mult = 1e3; s = s.slice(0, -1); }
  else if (s.endsWith("m")) { mult = 1e6; s = s.slice(0, -1); }
  // Strip thousand separators (dot or comma followed by exactly 3 digits)
  s = s.replace(/[.,](?=\d{3}(\D|$))/g, "");
  s = s.replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n * mult : 0;
}

function fmtEur(n, lang) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(lang === "es" ? "es-ES" : "en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

export const CapTableSimulator = ({ defaultPreMoney, lang }) => {
  const [preMoneyRaw, setPreMoneyRaw] = useState(
    defaultPreMoney ? String(defaultPreMoney) : ""
  );
  const [investmentRaw, setInvestmentRaw] = useState("");

  const preMoney = parseAmount(preMoneyRaw);
  const investment = parseAmount(investmentRaw);
  const postMoney = preMoney + investment;
  const investorPct = postMoney > 0 ? (investment / postMoney) * 100 : 0;
  const foundersPct = 100 - investorPct;
  const hasResult = preMoney > 0 && investment > 0;

  const inputCls =
    "w-full border border-[#E8E6E1] rounded-sm bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#2563EB] transition-colors";

  return (
    <div data-testid="captable-simulator">
      <div className="flex items-center gap-2">
        <Calculator className="h-4 w-4 text-[#B89154]" strokeWidth={1.5} />
        <Overline>{lang === "es" ? "Simulador de Cap Table" : "Cap Table simulator"}</Overline>
      </div>
      <p className="mt-2 text-xs text-[#5C5B5A] leading-relaxed">
        {lang === "es"
          ? "Calcula el equity del inversor según tu valoración pre-money."
          : "Calculate investor equity from your pre-money valuation."}
      </p>

      <label className="block mt-4 text-[10px] uppercase tracking-[0.18em] text-[#8A8886]">
        {lang === "es" ? "Valoración pre-money (€)" : "Pre-money valuation (€)"}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={preMoneyRaw}
        onChange={(e) => setPreMoneyRaw(e.target.value)}
        placeholder={lang === "es" ? "p. ej. 500000 o 1.5m" : "e.g. 500000 or 1.5m"}
        className={`mt-1.5 ${inputCls}`}
        data-testid="captable-premoney-input"
      />

      <label className="block mt-4 text-[10px] uppercase tracking-[0.18em] text-[#8A8886]">
        {lang === "es" ? "Inversión solicitada" : "Investment amount"}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={investmentRaw}
        onChange={(e) => setInvestmentRaw(e.target.value)}
        placeholder={lang === "es" ? "Importe libre, p. ej. 75000" : "Free amount, e.g. 75000"}
        className={`mt-1.5 ${inputCls}`}
        data-testid="captable-investment-input"
      />
      <div className="mt-2 grid grid-cols-4 gap-1.5">
        {SHORTCUTS.map((s) => {
          const isActive = investment === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setInvestmentRaw(String(s.value))}
              className={`px-1 py-1.5 text-xs border rounded-sm transition-colors ${
                isActive
                  ? "bg-[#2563EB] border-[#2563EB] text-[#F9F8F6]"
                  : "bg-white border-[#E8E6E1] text-[#5C5B5A] hover:border-[#2563EB] hover:text-[#2563EB]"
              }`}
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid={`captable-shortcut-${s.value / 1000}k`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {hasResult ? (
        <div className="mt-5 border border-[#E8E6E1] bg-white rounded-sm p-4" data-testid="captable-results">
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "founders", value: foundersPct },
                      { name: "investor", value: investorPct },
                    ]}
                    dataKey="value"
                    innerRadius={26}
                    outerRadius={44}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                    isAnimationActive={false}
                  >
                    <Cell fill={GREEN} />
                    <Cell fill={GOLD} />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[#5C5B5A] text-xs">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: GREEN }} />
                  {lang === "es" ? "Fundadores" : "Founders"}
                </span>
                <span className="font-medium text-[#1A1A1A]" data-testid="captable-founders-pct">
                  {foundersPct.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[#5C5B5A] text-xs">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: GOLD }} />
                  {lang === "es" ? "Inversor" : "Investor"}
                </span>
                <span className="font-medium text-[#1A1A1A]" data-testid="captable-investor-pct">
                  {investorPct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E8E6E1] flex items-center justify-between text-xs">
            <span className="uppercase tracking-[0.15em] text-[#8A8886]">Post-money</span>
            <span
              className="text-[#2563EB] font-medium"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="captable-postmoney"
            >
              {fmtEur(postMoney, lang)}
            </span>
          </div>
          <p className="mt-3 text-xs text-[#5C5B5A] leading-relaxed" data-testid="captable-summary">
            {lang === "es"
              ? `Con ${fmtEur(investment, lang)} a una pre-money de ${fmtEur(preMoney, lang)}, el inversor recibe el ${investorPct.toFixed(1)}% y los fundadores retienen el ${foundersPct.toFixed(1)}%.`
              : `With ${fmtEur(investment, lang)} at a ${fmtEur(preMoney, lang)} pre-money, the investor receives ${investorPct.toFixed(1)}% and founders retain ${foundersPct.toFixed(1)}%.`}
          </p>
        </div>
      ) : (
        <p className="mt-4 text-xs text-[#8A8886] italic" data-testid="captable-empty-hint">
          {lang === "es"
            ? "Introduce la valoración pre-money y un importe de inversión para ver el reparto de equity."
            : "Enter a pre-money valuation and an investment amount to see the equity split."}
        </p>
      )}
    </div>
  );
};
